import { ArrowRight } from 'lucide-react'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components'
import { useAnalytics } from 'hooks'
import { cn } from 'lib/utils'
import { useChatPrefillStore, useQueuedPromptStore } from '../../store/conversation-store'
import { getExplainerById, type Explainer, type ScoreRubric } from '../../process/score-explainers'
import { ScoreExplainerContext } from './score-explainer-context'

interface ScoreExplainerProviderProps {
  creativeId: string
  /** The rubric the scorecard is currently showing, for analytics. */
  rubric: ScoreRubric
  /** The judge's named highest-leverage fix, when the snapshot carries one. */
  singleFix?: string
  children: ReactNode
}

/**
 * Hosts the single explainer modal for one scorecard and exposes openExplainer to
 * its subtree. This is the expertise layer: tap any term to learn what it means,
 * why it moves the post, and fix it in one tap.
 */
export function ScoreExplainerProvider({
  creativeId,
  rubric,
  singleFix,
  children,
}: ScoreExplainerProviderProps) {
  const { track } = useAnalytics()
  const [open, setOpen] = useState<{ id: string; showAction: boolean } | null>(null)

  const openExplainer = useCallback(
    (id: string, opts?: { showAction?: boolean }) => {
      if (!getExplainerById(id, rubric)) return
      setOpen({ id, showAction: opts?.showAction ?? true })
      track(AnalyticsEvents.Composer.COMPOSER_SCORE_EXPLAINER_OPENED, {
        creative_id: creativeId,
        term: id,
        rubric,
      })
    },
    [creativeId, rubric, track]
  )

  const value = useMemo(() => ({ openExplainer }), [openExplainer])
  const explainer = open ? getExplainerById(open.id, rubric) : undefined

  return (
    <ScoreExplainerContext.Provider value={value}>
      {children}
      <Dialog open={!!explainer} onOpenChange={o => !o && setOpen(null)}>
        {explainer && (
          <ExplainerContent
            explainer={explainer}
            creativeId={creativeId}
            singleFix={singleFix}
            showAction={open?.showAction ?? true}
            onClose={() => setOpen(null)}
          />
        )}
      </Dialog>
    </ScoreExplainerContext.Provider>
  )
}

function ExplainerContent({
  explainer,
  creativeId,
  singleFix,
  showAction,
  onClose,
}: {
  explainer: Explainer
  creativeId: string
  singleFix?: string
  showAction: boolean
  onClose: () => void
}) {
  const { track } = useAnalytics()
  const setPrefill = useChatPrefillStore(s => s.setPrefill)
  const setQueuedPrompt = useQueuedPromptStore(s => s.setPrompt)

  // The one-fix verdict shows the judge's ACTUAL named fix when the snapshot has it.
  const namedFix = explainer.id === 'verdict.SHIP_WITH_ONE_FIX' ? singleFix : undefined

  const handleAction = () => {
    if (!explainer.action) return
    // With a named fix, mirror FixWithTakaCard: queue the prompt so it actually sends
    // (a bare prefill looked like the button did nothing). Otherwise keep the prefill.
    if (namedFix) setQueuedPrompt(creativeId, `Fix this: ${namedFix}`)
    else setPrefill(creativeId, explainer.action.prompt)
    track(AnalyticsEvents.Composer.COMPOSER_SCORE_EXPLAINER_ACTION_TAPPED, {
      creative_id: creativeId,
      term: explainer.id,
    })
    onClose()
  }

  return (
    <DialogContent variant="dark" size="sm" className="gap-0">
      <DialogHeader className="gap-2 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <DialogTitle variant="dark">{explainer.label}</DialogTitle>
          {explainer.weightNote && (
            <span className="bg-acid-tint rounded-full border border-acid px-2 py-0.5 text-[10px] font-semibold tracking-wide text-acid uppercase">
              {explainer.weightNote}
            </span>
          )}
        </div>
        <DialogDescription variant="dark">{explainer.oneLiner}</DialogDescription>
      </DialogHeader>

      {explainer.tags && explainer.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {explainer.tags.map(tag => (
            <span
              key={tag}
              className="mono rounded-[4px] bg-bg-3 px-1.5 py-0.5 text-[10px] tracking-[0.1em] text-paper-faint uppercase"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 border-t border-line pt-4">
        <p className="mono mb-1.5 text-[10px] tracking-[0.2em] text-paper-faint uppercase">
          Why it matters
        </p>
        <p className="text-[13.5px] leading-[1.6] text-paper-dim">{explainer.why}</p>
      </div>

      {namedFix && (
        <div className="mt-4 border-t border-line pt-4">
          <p className="mono mb-1.5 text-[10px] tracking-[0.2em] text-acid uppercase">The fix</p>
          <p className="text-[13.5px] leading-[1.6] text-paper-dim">{namedFix}</p>
        </div>
      )}

      {explainer.scale && <ScaleStrip low={explainer.scale.low} high={explainer.scale.high} />}

      {explainer.action && showAction && (
        <Button
          variant="t3-primary"
          size="t3-md"
          onClick={handleAction}
          className="mt-5 w-full justify-center gap-1.5"
        >
          {explainer.action.label}
          <ArrowRight size={15} />
        </Button>
      )}
    </DialogContent>
  )
}

/** A low → high read on the scale, mirroring the acid bar language of the breakdown. */
function ScaleStrip({ low, high }: { low: string; high: string }) {
  return (
    <div className="mt-4">
      <div className="flex gap-0.5" aria-hidden>
        {Array.from({ length: 10 }, (_, i) => (
          <span
            key={i}
            className="h-1.5 flex-1 rounded-full"
            style={{
              backgroundColor: 'var(--acid)',
              opacity: 0.25 + (i / 9) * 0.75,
            }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-col gap-1.5">
        <ScaleRow tone="low" text={low} />
        <ScaleRow tone="high" text={high} />
      </div>
    </div>
  )
}

function ScaleRow({ tone, text }: { tone: 'low' | 'high'; text: string }) {
  return (
    <div className="flex items-start gap-2">
      <span
        className={cn(
          'mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full',
          tone === 'high' ? 'bg-acid' : 'bg-bg-4'
        )}
      />
      <span className="text-[12px] leading-[1.5] text-paper-dim">{text}</span>
    </div>
  )
}
